import HighchartsReact from "highcharts-react-official";
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from "react";
import Highcharts from "highcharts";
import { useLocation } from "react-router-dom";
import iv from "implied-volatility";
import { Tooltip as ReactTooltip } from "react-tooltip";
import patternFill from "highcharts/modules/pattern-fill";
import { FiMinus } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import { blackScholes } from "black-scholes";
import TargetSlidersMobile from "./TargetSliderMobile";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";

patternFill(Highcharts);

const StrategyGraph = ({
  ltp,
  options,
  data,
  greeksData,
  greeksJson,
  selectedData,
  atmIndex,
  leastExpiry,
  ltptoIV,
  isParamsValid,
  setGreeksData,
  isPositions,
  isDark,
  symbolData,
  selectedSymbol,
  currentOptionsData,
  selectedExpiry,
}) => {
  const { subscribeToSymbols, unsubscribeToSymbols } = useContext(
    WebsocketWrapperContext
  );

  const [selectedTab, setSelectedTab] = useState("Payoff");
  const tabs = ["Payoff", "P&L Table", "Greeks", ""];
  const [lotsValue, setLotsValue] = useState(1);
  const [calculatedIV, setCalculatedIV] = useState(0);
  const [pointerStrike, setPointerStrike] = useState(null);
  const [selectedExpiryDates, setSelectedExpiryDates] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const lastPointerUpdate = useRef(0);
  const x_axis = data?.["x_axis"];
  const y_axis = data?.["y_axis"];
  const theme = "light";
  const array = data?.["y_axis"];
  const [lowerBand1Val, setLowerBand1Val] = useState(0);
  const [lowerBand2Val, setLowerBand2Val] = useState(0);
  const [upperBand1Val, setUpperBand1Val] = useState(0);
  const [upperBand2Val, setUpperBand2Val] = useState(0);
  const [targetPnLLine, setTargetPnLLine] = useState(data?.["y_axis"]);
  const [daysleft, setdaysleft] = useState(0);
  const [daysCount, setdaysCount] = useState(0);
  const [daysToExpiry, setDaysToExpiry] = useState(0);
  const [expiries, setExpiries] = useState([]);
  const [selectedDate, setDate] = useState(new Date());
  const [ltpLine, setLtpLine] = useState(0);
  const chartComponent = useRef(null);
  const prevYAxis = useRef();
  const prevCalculatedIV = useRef();
  const prevLotsValue = useRef();
  const prevTheme = useRef();
  const prevTargetPnLLine = useRef();
  const subscribedCodesRef = useRef([]);
  const [linechart, setlineChart] = useState({
    xAxis: {
      tickInterval: 100,
      categories: x_axis,
      title: false,
      labels: {
        enabled: true,
        rotation: 0,
        style: {
          textOverflow: "none",
        },
        formatter: function () {
          let chartWidth = this.chart.plotWidth;
          let labelCount = this.axis.tickPositions.length;
          let labelWidth = chartWidth / labelCount;
          let showLabels = labelCount;

          // Adjust the number of labels to show based on label width
          if (labelWidth < 100) {
            // Adjust the threshold as needed
            showLabels = Math.ceil(chartWidth / 100); // Show 1 label per 50 pixels
          }

          // Show only every nth label to fit the available space
          let index = this.axis.tickPositions.indexOf(this.pos);
          if (index % Math.ceil(labelCount / showLabels) !== 0) {
            return ""; // Return an empty string to skip label
          }

          return this.value;
        },
      },
    },
    yAxis: {
      title: {
        enabled: false,
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
      },
    },
    series: [
      {
        name: "",
        color: "#D8FDE2",
        negativeColor: "#FFD9D7",
        showInLegend: false,
      },
    ],
    chart: {
      type: "area",
      height: 400,
      backgroundColor: "none",
    },
    title: {
      text: "",
    },
  });

  const findNearestIndex = (arr, val) => {
    if (arr.length === 0) return -1; // Return -1 if the array is empty

    let closestIndex = 0;
    let minDiff = Math.abs(arr[0] - val);
    for (let i = 1; i < arr.length; i++) {
      const diff = Math.abs(arr[i] - val);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }
    return closestIndex;
  };

  const handleLtpLineChange = (ltp) => {
    if (data?.["x_axis"] && data?.["x_axis"].length > 0) {
      setLtpLine(findNearestIndex(data["x_axis"], ltp));
    }
  };

  useEffect(() => {
    if (data?.ltpIndex !== undefined) {
      setLtpLine(data?.ltpIndex);
    }
  }, [data]);

  const customPattern = (color, borderColor) => {
    return {
      pattern: {
        path: {
          d: "M 0 0 L 10 0 ", // Inner path
          stroke: color,
          strokeWidth: 2,
        },
        width: 10,
        height: 10,
        patternTransform: "rotate(-50)",
      },
    };
  };

  function padLotsValue(value) {
    return value.toString().padStart(2, "0");
  }

  const [chartOptions, setChartOptions] = useState(linechart);

  useLayoutEffect(() => {
    let dt = new Date(leastExpiry);
    let varb = 0;
    let leastdate = new Date(leastExpiry + " 15:29:59");
    let g;
    selectedData.map((o) => {
      if (o == leastExpiry) {
        varb = true;
      }
      let ddate;
      if (o.hasOwnProperty("expiry")) {
        ddate = new Date(o.expiry);
      } else {
        ddate = new Date(o.expiry_date);
      }
      leastdate = new Date(Math.min(ddate, leastdate));
    });
    if (!varb) {
      dt = new Date(leastdate);
      dt.setHours(0, 0, 0, 0); // Remove time component from dt
      setDate(dt);

      let exd = new Date(leastdate);
      exd.setHours(0, 0, 0, 0); // Remove time component from exd

      // Calculate the difference in days
      g = Math.round((dt - exd) / (1000 * 60 * 60 * 24));
      setdaysleft(g);
    }

    let td = new Date();
    let dates = [];
    let date = new Date();
    while (date < dt) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    dates.push(new Date(date));
    // // console.log(dt);
    setExpiries(dates);

    let exd = new Date();
    let daysCount = Math.round((date - exd) / (1000 * 60 * 60 * 24) + 1);
    setdaysCount(daysCount);

    let finalExpiry = new Date(leastExpiry);
    let daysExpiry = Math.round(
      (finalExpiry - exd) / (1000 * 60 * 60 * 24) + 1
    );
    setDaysToExpiry(daysExpiry);

    updateTargetLine(g, daysExpiry);
  }, [leastExpiry, data]);

  const handleDateClick = (event) => {
    let date = new Date(event);
    date.setHours(0, 0, 0, 0);

    setDate(date);

    let exd = new Date(leastExpiry);
    exd.setHours(0, 0, 0, 0);
    let g = Math.round((exd - date) / (1000 * 60 * 60 * 24));
    setdaysleft(g);

    exd = new Date();
    let daysCount = Math.round((date - exd) / (1000 * 60 * 60 * 24) + 1);
    setdaysCount(daysCount);

    let finalExpiry = new Date(leastExpiry);
    let daysExpiry = Math.round(
      (finalExpiry - exd) / (1000 * 60 * 60 * 24) + 1
    );
    setDaysToExpiry(daysExpiry);
    // Computing target line

    updateTargetLine(g, daysExpiry);
    updateChart();
  };

  function setAtmIndex() {
    let index = 0;
    try {
      if (atmIndex) {
        const targetDateTimeString = atmIndex.expiry_date + " 15:29:59"; // Target date and time
        const targetDateTime = new Date(targetDateTimeString);
        const currentDate = new Date();
        let timeToExpiration = Math.max(
          0,
          (targetDateTime - currentDate) / (24 * 60 * 60 * 365 * 1000) ||
            0.0002 / 365
        );
        let computedIv = (
          computeIV(ltptoIV, ltp, atmIndex?.strike, timeToExpiration, "call") *
          100
        ).toFixed(2);
        setCalculatedIV(computedIv);
      }
    } catch (error) {}
  }

  const closestIndex = (band, x_axis) => {
    const index = x_axis?.reduce(
      (prevIndex, currentValue, currentIndex, arr) => {
        return Math.abs(currentValue - band) < Math.abs(arr[prevIndex] - band)
          ? currentIndex
          : prevIndex;
      },
      0
    );
    return index;
  };

  function computeTargetLine(
    spot,
    row,
    timeToExpiration,
    annualizedDaysCount,
    optionType
  ) {
    const riskFreeRate = 0.064515;
    let targetPrices = [];
    let rowIv = computeIV(
      parseFloat(row?.price),
      parseFloat(spot),
      row?.strike,
      annualizedDaysCount,
      optionType
    ).toFixed(2);
    if (parseInt(rowIv) > 1) {
      rowIv = rowIv / 100;
    }
    for (let val = 0; val < x_axis?.length; val++) {
      const optionPrice = blackScholes(
        x_axis[val],
        row?.strike,
        timeToExpiration,
        rowIv,
        riskFreeRate,
        optionType
      );
      targetPrices.push(parseFloat(Math.round(optionPrice, 2)));
    }
    return targetPrices;
  }

  function computeIV(optionPrice, spot, strike, timeToExpiration, optionType) {
    const riskFreeRate = 0.064515;
    const initialGuessIv = iv.getImpliedVolatility(
      optionPrice,
      spot,
      strike,
      timeToExpiration,
      riskFreeRate,
      optionType
    );
    return initialGuessIv;
  }

  const calculatePlotBandWidth = (
    days_to_expire,
    ltp,
    calculatedIV,
    x_axis
  ) => {
    if (
      calculatedIV === undefined ||
      calculatedIV === null ||
      calculatedIV === 0
    ) {
      return {
        lowerBand1: null,
      };
    }

    const width = parseFloat(
      ltp * (calculatedIV / 100) * Math.sqrt(days_to_expire / 365)
    );

    const lowerBand1 = parseFloat(ltp) - width;
    const upperBand1 = parseFloat(ltp) + width;
    const lowerBand2 = parseFloat(ltp) - width * 2;
    const upperBand2 = parseFloat(ltp) + width * 2;

    const lowerBand1Index = closestIndex(lowerBand1, x_axis);
    const upperBand1Index = closestIndex(upperBand1, x_axis);
    const lowerBand2Index = closestIndex(lowerBand2, x_axis);
    const upperBand2Index = closestIndex(upperBand2, x_axis);

    // Get the actual x-axis values using the indices
    const lowerBand1Value = x_axis[lowerBand1Index];
    const upperBand1Value = x_axis[upperBand1Index];
    const lowerBand2Value = x_axis[lowerBand2Index];
    const upperBand2Value = x_axis[upperBand2Index];
    setLowerBand1Val(lowerBand1Value);
    setLowerBand2Val(lowerBand2Value);
    setUpperBand1Val(upperBand1Value);
    setUpperBand2Val(upperBand2Value);

    return {
      lowerBand1,
      upperBand1,
      lowerBand2,
      upperBand2,
      lowerBand1Index,
      upperBand1Index,
      lowerBand2Index,
      upperBand2Index,
    };
  };

  function alignMultiExpiryOIToXAxis(
    currentOptionsData,
    selectedExpiryDates,
    x_axis
  ) {
    if (
      !currentOptionsData ||
      !selectedExpiryDates?.length ||
      !x_axis?.length
    ) {
      return { callOIData: [], putOIData: [], maxOI: 0 };
    }
    const callOIData = Array(x_axis.length).fill(0);
    const putOIData = Array(x_axis.length).fill(0);

    if (!currentOptionsData || !selectedExpiryDates || !x_axis) {
      return { callOIData, putOIData };
    }

    // Collect all available strikes and their OI data from all selected expiry dates
    const allStrikesWithOI = new Map(); // Map<strike, {callOI, putOI}>
    selectedExpiryDates.forEach((expiryDate) => {
      const options = currentOptionsData[expiryDate]?.options;
      if (!options) {
        return;
      }

      options.forEach((option) => {
        const strike = parseFloat(
          option.strike || option.call?.strike || option.put?.strike
        );
        if (isNaN(strike)) return;

        let callOI = 0;
        let putOI = 0;

        // Try to get OI data from symbolData
        if (option.call?.code && symbolData[option.call?.code]) {
          callOI = Number(symbolData[option.call?.code]?.oi || 0);
        }
        if (option.put?.code && symbolData[option.put?.code]) {
          putOI = Number(symbolData[option.put?.code]?.oi || 0);
        }

        // Fallback: try to get OI from the option object itself if available
        if (callOI === 0 && option.call?.oi) {
          callOI = Number(option.call.oi);
        }
        if (putOI === 0 && option.put?.oi) {
          putOI = Number(option.put.oi);
        }

        // Debug symbolData access

        // Aggregate OI data for this strike across all expiry dates
        if (allStrikesWithOI.has(strike)) {
          const existing = allStrikesWithOI.get(strike);
          existing.callOI += callOI;
          existing.putOI += putOI;
        } else {
          allStrikesWithOI.set(strike, { callOI, putOI });
        }
      });
    });

    // Calculate maxOI for scaling
    const maxOI = Math.max(
      ...Array.from(allStrikesWithOI.values()).map((d) =>
        Math.max(d.callOI, d.putOI)
      )
    );
    const maxProfit = Math.max(...y_axis);
    const minProfit = Math.min(...y_axis);

    // Calculate the minimum height in y-axis units (45px minimum for highest bar)
    // Assuming chart height is 400px, calculate what 45px represents in y-axis units
    const chartHeight = 400; // Chart height in pixels
    const minHeightPixels = 100; // Minimum height in pixels for highest bar
    const yAxisRange = maxProfit - minProfit;
    const minHeightInYAxisUnits = (minHeightPixels / chartHeight) * yAxisRange;

    // Now iterate through x_axis and find closest strike for each x_axis value
    x_axis.forEach((xValue, xIndex) => {
      let closestStrike = null;
      let minDistance = Infinity;

      // Find the closest strike to this x_axis value
      allStrikesWithOI.forEach((oiData, strike) => {
        const distance = Math.abs(strike - xValue);
        if (distance < minDistance) {
          minDistance = distance;
          closestStrike = strike;
        }
      });

      // Assign OI data from closest strike to this x_axis position
      if (closestStrike !== null) {
        const oiData = allStrikesWithOI.get(closestStrike);

        // Scale OI values so the maximum OI bar reaches at least the minimum height
        // If the natural scaling would make the highest bar too small, use minimum height scaling
        let scaleFactor;
        if (maxOI > 0) {
          const naturalScaleFactor = maxProfit / maxOI;
          const minScaleFactor = minHeightInYAxisUnits / maxOI;
          // Use the larger of the two scale factors to ensure minimum height
          scaleFactor = Math.max(naturalScaleFactor, minScaleFactor);
        } else {
          scaleFactor = 1;
        }

        callOIData[xIndex] = oiData.callOI * scaleFactor;
        putOIData[xIndex] = oiData.putOI * scaleFactor;
      }
    });

    return { callOIData, putOIData, maxOI };
  }

  const updateChart = () => {
    if (!x_axis?.length || !y_axis?.length || !data) {
      return; // Don't update chart with invalid data
    }
    const {
      lowerBand1,
      upperBand1,
      lowerBand2,
      upperBand2,
      lowerBand1Index,
      upperBand1Index,
      lowerBand2Index,
      upperBand2Index,
    } = calculatePlotBandWidth(lotsValue, ltp, calculatedIV, x_axis);

    if (!x_axis?.length || !y_axis?.length) {
      return; // Don't update chart with invalid data
    }

    if (((upperBand1 == upperBand2) == lowerBand1) == lowerBand2) {
      return;
    }

    // Compute OI data for bar graph
    let callOIData = [];
    let putOIData = [];
    let maxOI = 0;
    let maxProfit = Math.max(...y_axis);

    if (currentOptionsData && selectedExpiryDates.length > 0 && x_axis) {
      const aligned = alignMultiExpiryOIToXAxis(
        currentOptionsData,
        selectedExpiryDates,
        x_axis
      );
      callOIData = aligned.callOIData;
      putOIData = aligned.putOIData;
      maxOI = aligned.maxOI;
    } else {
      callOIData = Array(x_axis?.length || 0).fill(0);
      putOIData = Array(x_axis?.length || 0).fill(0);
    }

    // Pre-compute OI scale factor for secondary Y-axis labels
    const _maxProfit = Math.max(...y_axis);
    const _minProfit = Math.min(...y_axis);
    const _yAxisRange = _maxProfit - _minProfit;
    const _chartHeightPx = 400;
    const _minBarHeightPx = 100;
    const _minHeightInYAxisUnits =
      (_minBarHeightPx / _chartHeightPx) * _yAxisRange;
    const oiScaleFactor =
      maxOI > 0
        ? Math.max(_maxProfit / maxOI, _minHeightInYAxisUnits / maxOI)
        : null;

    setChartOptions((prevOptions) => ({
      ...prevOptions,
      // Store maxOI and maxProfit for use in y-axis formatters
      maxOI: maxOI,
      maxProfit: maxProfit,
      chart: {
        marginTop: 20,
        style: {
          cursor: "crosshair",
        },
        animation: false, // Disable animations that might cause opacity changes
        events: {
          load() {
            const chart = this;

            // Clear any existing custom plot lines
            if (chart.customPlotLine) {
              chart.customPlotLine.destroy();
            }

            const plotLineX = chart.xAxis[0].toPixels(data?.ltpIndex, true);
            const plotLineY1 = chart.yAxis[0].toPixels(
              chart.yAxis[0].min,
              true
            );
            const plotLineY2 = chart.yAxis[0].toPixels(
              chart.yAxis[0].max,
              true
            );
            // Draw the custom plot line and store reference

            // Store chart reference for external access
            chartComponent.current = chart;
          },
        },
      },
      series: [
        {
          type: "area",
          data: y_axis,
          name: "Profit/Loss",
          color: "#B1E8B4B3",
          opacity: 0.7,
          negativeColor: "#F49B9BB3",
          lineWidth: 2,
          showInLegend: false,
          zIndex: 2,
          marker: {
            enabled: false,
          },
          threshold: 0,
          fillOpacity: 0.4,
          trackByArea: true,
          connectNulls: false,
          step: false,
          states: {
            inactive: {
              opacity: 0.4,
            },
            hover: {
              opacity: 0.4,
            },
          },
        },
        {
          type: "line",
          data: targetPnLLine,
          name: "Target P&L",
          color: "#0075FF",
          opacity: 0.5,
          negativeColor: "#0075FF",
          lineWidth: 2,
          showInLegend: false,
          zIndex: 2,
          marker: {
            enabled: false,
          },
          states: {
            inactive: {
              opacity: 0.5,
            },
            hover: {
              opacity: 0.5,
            },
          },
        },
        ...(selectedExpiryDates.length > 0
          ? [
              {
                name: "Put OI",
                type: "column",
                data: putOIData,
                showInLegend: false,
                color: "#B1E8B4B3",
                opacity: 0.8,
                grouping: true,
                enableMouseTracking: false,
                yAxis: 0,
                pointPlacement: "on",
                states: {
                  inactive: {
                    opacity: 0.7,
                  },
                  hover: {
                    opacity: 0.7,
                  },
                },
              },
              {
                name: "Call OI",
                type: "column",
                data: callOIData,
                showInLegend: false,
                color: "#F49B9BB3",
                grouping: true,
                opacity: 0.8,
                enableMouseTracking: false,
                yAxis: 0,
                pointPlacement: "on",
                states: {
                  inactive: {
                    opacity: 0.7,
                  },
                  hover: {
                    opacity: 0.7,
                  },
                },
              },
            ]
          : []),
      ],
      yAxis: [
        {
          gridLineColor: "#EFEFEF",
          tickInterval: 100,
          gridLineWidth: 0,
          minorGridLineWidth: 0,
          labels: {
            formatter: function () {
              let chartWidth = this.chart.plotHeight;
              let labelCount = this.axis.tickPositions.length;
              let labelWidth = chartWidth / labelCount;
              let showLabels = labelCount;

              if (labelWidth < 100) {
                showLabels = Math.ceil(chartWidth / 100);
              }
              let index = this.axis.tickPositions.indexOf(this.pos);
              if (index % Math.ceil(labelCount / showLabels) !== 0) {
                return "";
              }

              return this.value.toLocaleString();
            },
            style: {
              color: theme === "light" ? "#575757" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
              fontFamily: "Graphik, sans-serif",
            },
          },
          plotLines: [
            {
              value: 0,
              width: 2,
              color: "#efefef",
            },
          ],
          title: {
            enabled: true,
            text: "Profit / Loss",
            style: {
              color: theme === "light" ? "#B7B7B7" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
            },
            x: 0,
            align: "middle",
          },
          opposite: false,
        },
        {
          // Mirror primary axis range but show 4 evenly spaced ticks on both sides
          min: Math.min(...y_axis),
          max: Math.max(...y_axis),
          tickPositions: (() => {
            const min = Math.min(...y_axis);
            const max = Math.max(...y_axis);
            const range = max - min;
            if (!isFinite(range) || range === 0) return [min, max];
            return [0, 1, 2, 3].map((i) => min + (i * range) / 3);
          })(),
          // Provide scale to formatter via axis options
          custom: { oiScaleFactor: oiScaleFactor },
          labels: {
            formatter: function () {
              const scaleFactor = this.axis?.options?.custom?.oiScaleFactor;
              if (!scaleFactor) return "";
              const sign = this.value < 0 ? "-" : "";
              const oiValue = Math.abs(this.value) / scaleFactor;
              if (oiValue >= 10000000) {
                return sign + (oiValue / 10000000).toFixed(2) + "Cr";
              } else if (oiValue >= 100000) {
                return sign + (oiValue / 100000).toFixed(2) + "L";
              } else if (oiValue >= 1000) {
                return sign + (oiValue / 1000).toFixed(1) + "K";
              }
              return sign + Math.round(oiValue).toLocaleString();
            },
            style: {
              color: theme === "light" ? "#575757" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
              fontFamily: "Graphik, sans-serif",
            },
          },
          title: {
            text: "Open Interest",
            style: {
              color: theme === "light" ? "#B7B7B7" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
            },
            x: 0,
            align: "middle",
          },
          opposite: true,
          gridLineWidth: 0,
          minorGridLineWidth: 0,
        },
      ],
      plotOptions: {
        series: {
          inactiveOpacity: 1, // Prevent other series from becoming transparent on hover
          states: {
            inactive: {
              opacity: 1, // Keep full opacity even when inactive
            },
            hover: {
              opacity: 1, // Keep full opacity on hover
            },
          },
          point: {
            events: {
              mouseOver: function () {
                if (x_axis && x_axis[this.x]) {
                  // Use the raw x_axis value (number) instead of formatted category
                  setPointerStrike(x_axis[this.x]);
                }
              },
              mouseOut: function () {
                setPointerStrike(null);
              },
            },
            states: {
              hover: {
                enabled: true,
                brightness: 0.2,
              },
            },
          },
        },
        column: {
          pointPadding: 0,
          groupPadding: 0,
          borderRadius: 2,
          grouping: true,
        },
      },
      xAxis: {
        lineColor: "transparent",
        gridLineColor: "#EFEFEF",
        tickInterval: 1,
        allowDecimals: false,
        categories: x_axis.map((value) => value.toLocaleString("en-IN")),
        labels: {
          style: {
            color: theme === "light" ? "#6F6F6F" : "#FFF",
            fontSize: "10px",
            fontWeight: "500",
            fontFamily: "Graphik, sans-serif",
          },
          y: 20,
          x: 0,
          rotation: -45,
          step: 2,
        },
        plotLines: [
          {
            color: isDark ? "#ffffff" : "#0F0F0F",
            width: 2,
            value: ltpLine,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: lowerBand2Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: lowerBand1Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: upperBand1Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: upperBand2Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
        ],
        plotBands: [
          {
            color: customPattern("transparent"),
            from: lowerBand2Index,
            to: upperBand2Index,
            zIndex: 1,
            label: {
              text: "-2SD",
              align: "left",
              x: -18,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
              },
            },
          },
          {
            color: customPattern("#B7B7B7"),
            from: lowerBand1Index,
            to: upperBand1Index,
            zIndex: 1,
            position: "absolute",

            label: {
              text: "-1SD",
              align: "left",
              x: -10,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
              },
            },
          },
          {
            color: customPattern("transparent"),
            from: lowerBand2Index,
            to: upperBand2Index,
            zIndex: 1,
            label: {
              text: "+2SD",
              align: "right",
              x: 15,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
                overflow: "none",
              },
            },
          },
          {
            color: customPattern("#CFCFCF"),
            from: lowerBand1Index,
            to: upperBand1Index,
            zIndex: 1,
            label: {
              text: "+1SD",
              align: "right",
              x: 5,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
              },
            },
          },
        ],
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        formatter: function () {
          return `<b>Strike: ${this.x}</b><br/>P&L: ${this.y}`;
        },
        hideDelay: 0,
        followPointer: false,
        animation: false,
      },
    }));
  };

  function updateTargetLine(g, daysCount) {
    let xaxis_length = x_axis?.length;
    let targetLine = Array.from({ length: xaxis_length }, () => 0);
    for (let val = 0; val < selectedData.length; val++) {
      let optionType = selectedData[val]?.isCALL ? "call" : "put";
      let ddate;
      if (selectedData[val].hasOwnProperty("expiry")) {
        ddate = new Date(selectedData[val].expiry);
      } else {
        ddate = new Date(selectedData[val].expiry_date);
      }
      let date = new Date();
      date.setDate(date.getDate() + daysCount);
      let expiry_days = Math.round((ddate - date) / (1000 * 60 * 60 * 24));
      expiry_days = expiry_days < 0 ? 0 : expiry_days;
      let timeToExpiration = Math.max(
        0,
        Math.abs(expiry_days + g) / 365 || 0.0002 / 365
      );
      let annualizedDaysCount = Math.max(
        0,
        (daysCount + expiry_days) / 365 || 0.0002 / 365
      );

      let line = computeTargetLine(
        ltp,
        selectedData[val],
        timeToExpiration,
        annualizedDaysCount,
        optionType
      );
      let updatedLine;
      let optionPrice = parseFloat(selectedData[val]?.price);
      let total_quantity =
        selectedData[val]?.lotSize * selectedData[val]?.lot_multiplier;
      if (selectedData[val]?.isBuy) {
        updatedLine = line.map((item) => (item - optionPrice) * total_quantity);
      } else {
        updatedLine = line.map((item) => (optionPrice - item) * total_quantity);
      }
      targetLine.map(
        (value, index) => (targetLine[index] += updatedLine[index])
      );

      let currentPriceIndex = 0;
      let minDiff = Math.abs(ltp - x_axis?.[0]);
      for (let i = 1; i < x_axis?.length; i++) {
        let diff = Math.abs(ltp - x_axis[i]);
        if (diff < minDiff) {
          minDiff = diff;
          currentPriceIndex = i;
        }
      }
      if (currentPriceIndex == -1) {
        currentPriceIndex = parseInt(x_axis.length / 2);
      }
      selectedData[val]["targetPrice"] = parseFloat(
        line[currentPriceIndex]
      ).toFixed(2);
    }

    const hasNaN = targetLine.some((value) => isNaN(value));
    if (!hasNaN) {
      setTargetPnLLine(targetLine);
    } else {
      if (targetPnLLine != data?.["y_axis"]) {
        setTargetPnLLine(data?.["y_axis"]);
      }
    }
  }

  const decrementLots = () => {
    if (lotsValue > 1) {
      setLotsValue(lotsValue - 1);
    }
  };

  const incrementLots = () => {
    setLotsValue(lotsValue + 1);
  };

  // Initialize selected expiry dates with current expiry
  useEffect(() => {
    if (selectedExpiry && !selectedExpiryDates.includes(selectedExpiry)) {
      setSelectedExpiryDates([selectedExpiry]);
    }
  }, [selectedExpiry]);

  // Helper function to get option codes from selected expiry dates
  const getOptionCodesFromSelectedExpiries = (
    selectedExpiries,
    optionsData
  ) => {
    if (!selectedExpiries || !optionsData) return [];

    const codes = [];
    selectedExpiries.forEach((expiryDate) => {
      const expiryData = optionsData[expiryDate];
      if (expiryData?.options) {
        expiryData.options.forEach((option) => {
          if (option.call?.code) codes.push(option.call.code);
          if (option.put?.code) codes.push(option.put.code);
        });
      }
    });
    return codes;
  };

  // Subscribe to symbols when selected expiry dates change
  useEffect(() => {
    if (
      selectedExpiryDates.length > 0 &&
      selectedSymbol &&
      currentOptionsData
    ) {
      const optionCodes = getOptionCodesFromSelectedExpiries(
        selectedExpiryDates,
        currentOptionsData
      );
      if (optionCodes.length > 0) {
        let exchange =
          currentOptionsData[selectedExpiryDates[0]]?.options[0]?.call
            ?.exchange;
        subscribeToSymbols(optionCodes, exchange || "NSE");
        subscribedCodesRef.current = optionCodes;
      }
    }
    if (y_axis?.length > 0 && x_axis?.length > 0) {
      updateChart();
    }

    // Cleanup function to unsubscribe from previous codes
    return () => {
      if (
        subscribedCodesRef.current.length > 0 &&
        selectedSymbol &&
        unsubscribeToSymbols
      ) {
        unsubscribeToSymbols(
          subscribedCodesRef.current,
          selectedSymbol.exchange || "NSE"
        );
        subscribedCodesRef.current = [];
      }
    };
  }, [selectedExpiryDates]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (
        subscribedCodesRef.current.length > 0 &&
        selectedSymbol &&
        unsubscribeToSymbols
      ) {
        unsubscribeToSymbols(
          subscribedCodesRef.current,
          selectedSymbol.exchange || "NSE"
        );
        subscribedCodesRef.current = [];
      }
    };
  }, [selectedSymbol, unsubscribeToSymbols]);
  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (prevYAxis.current !== undefined) {
      if (prevYAxis.current !== y_axis) {
      }
      if (prevCalculatedIV.current !== calculatedIV) {
      }
      if (prevLotsValue.current !== lotsValue) {
      }
      if (prevTheme.current !== theme) {
      }
      if (prevTargetPnLLine.current !== targetPnLLine) {
      }
    }

    // Update refs after checking
    prevYAxis.current = y_axis;
    prevCalculatedIV.current = calculatedIV;
    prevLotsValue.current = lotsValue;
    prevTheme.current = theme;
    prevTargetPnLLine.current = targetPnLLine;
    if (y_axis?.length > 0 && x_axis?.length > 0) {
      setAtmIndex();
      updateChart();
    }
  }, [y_axis, calculatedIV, targetPnLLine]);

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = daysOfWeek[date.getDay()];
    const day = date.toLocaleDateString(undefined, { day: "2-digit" });
    const month = date.toLocaleDateString(undefined, { month: "short" });
    const year = date.toLocaleDateString(undefined, { year: "2-digit" });

    return `${dayOfWeek}, ${day} ${month} ${year}`;
  };
  // // console.log("greks", greeksJson);

  // Helper function to render OI legend content
  const renderOILegend = (targetOption) => {
    // Handle case where targetOption is undefined or doesn't have call/put structure (e.g., futures)
    if (!targetOption || (!targetOption.call && !targetOption.put)) {
      return null;
    }

    // Calculate aggregated OI from all selected expiry dates
    let totalCallOI = 0;
    let totalPutOI = 0;

    selectedExpiryDates.forEach((expiryDate) => {
      const options = currentOptionsData[expiryDate]?.options;
      if (!options) return;

      const targetOptionInExpiry = options.find((option) => {
        const optionStrike = parseFloat(
          option.call?.strike || option.put?.strike
        );
        const targetStrike = parseFloat(
          targetOption.call?.strike || targetOption.put?.strike
        );
        return Math.abs(optionStrike - targetStrike) < 0.01;
      });

      if (targetOptionInExpiry) {
        if (symbolData[targetOptionInExpiry.call?.code]) {
          totalCallOI += Number(
            symbolData[targetOptionInExpiry.call?.code]?.oi || 0
          );
        }
        if (symbolData[targetOptionInExpiry.put?.code]) {
          totalPutOI += Number(
            symbolData[targetOptionInExpiry.put?.code]?.oi || 0
          );
        }
      }
    });

    const formatVolume = (volume) => {
      if (volume >= 10000000) {
        return `${(volume / 10000000).toFixed(2)}Cr`;
      } else if (volume >= 100000) {
        return `${(volume / 100000).toFixed(2)}L`;
      } else if (volume >= 1000) {
        return `${(volume / 1000).toFixed(2)}K`;
      } else {
        if (isNaN(volume)) {
          return "0";
        }
        return `${volume}`;
      }
    };

    const strikeValue = parseFloat(
      targetOption.call?.strike || targetOption.put?.strike
    );

    return (
      <div className="flex items-center gap-4 text-xs">
        <div className="text-p300">
          OI data at <span className="font-bold">{strikeValue}</span>
          {pointerStrike && (
            <span className="ml-1 text-blue-500 text-[10px] opacity-75">●</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-pink-300 rounded-sm"></div>
          <span className="text-p300">Call OI:</span>
          <span className="font-bold">{formatVolume(totalCallOI)}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-green-300 rounded-sm"></div>
          <span className="text-p300">Put OI:</span>
          <span className="font-bold">{formatVolume(totalPutOI)}</span>
        </div>
      </div>
    );
  };

  // Add OI legend component
  const OILegend = React.useMemo(() => {
    if (!symbolData || !selectedSymbol || !currentOptionsData) {
      return null;
    }

    // Show placeholder when no expiry dates are selected
    if (selectedExpiryDates.length === 0) {
      return (
        <div className="flex items-center gap-4 text-xs">
          <div className="text-gray-500">
            Select expiry dates to view OI data
          </div>
        </div>
      );
    }

    // Get options from the first selected expiry for strike reference
    const firstExpiry = selectedExpiryDates[0];
    const options = currentOptionsData[firstExpiry]?.options;
    if (!options || !ltp) return null;

    // Use pointer strike if available, otherwise use closest strike to LTP
    let targetStrike = pointerStrike;

    if (!targetStrike) {
      // Fallback to closest strike to current LTP
      let closestStrike = options[0]?.strike;
      let minDiff = Math.abs(ltp - closestStrike);

      options.forEach((option) => {
        const strike = parseFloat(option.call?.strike || option.put?.strike);
        const diff = Math.abs(ltp - strike);
        if (diff < minDiff) {
          minDiff = diff;
          closestStrike = strike;
        }
      });
      targetStrike = closestStrike;
    }

    // Get OI data for the target strike - use more flexible matching
    const targetOption = options.find((option) => {
      const optionStrike = parseFloat(
        option.call?.strike || option.put?.strike
      );
      const targetStrikeNum = parseFloat(targetStrike);
      // Use approximate matching to handle floating point precision issues
      const match = Math.abs(optionStrike - targetStrikeNum) < 0.01;
      return match;
    });

    if (!targetOption) {
      // Fallback: find the closest available strike
      const targetStrikeNum = parseFloat(targetStrike);
      let closestOption = options[0];
      let minDiff = Math.abs(
        parseFloat(options[0]?.call?.strike || options[0]?.put?.strike) -
          targetStrikeNum
      );

      options.forEach((option) => {
        const optionStrike = parseFloat(
          option.call?.strike || option.put?.strike
        );
        const diff = Math.abs(optionStrike - targetStrikeNum);
        if (diff < minDiff) {
          minDiff = diff;
          closestOption = option;
        }
      });

      return renderOILegend(closestOption);
    }

    return renderOILegend(targetOption);
  }, [
    symbolData,
    selectedSymbol,
    currentOptionsData,
    selectedExpiry,
    ltp,
    pointerStrike,
    selectedExpiryDates,
  ]);

  // Get all available expiry dates
  const availableExpiryDates = React.useMemo(() => {
    if (!currentOptionsData) return [];
    const dates = Object.keys(currentOptionsData).sort((a, b) => {
      // Sort dates properly by converting to Date objects
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB; // Ascending order (earliest to latest)
    });
    return dates;
  }, [currentOptionsData]);

  // Handle expiry date selection
  const handleExpiryToggle = (expiryDate) => {
    setSelectedExpiryDates((prev) => {
      const newSelection = prev.includes(expiryDate)
        ? prev.filter((date) => date !== expiryDate)
        : [...prev, expiryDate];
      return newSelection;
    });
  };

  // Format date for display
  const formatExpiryDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  };

  return (
    <>
      {isParamsValid ? (
        <>
          <div
            className="w-[100%] h-auto flex flex-col rounded-md bg-p400 shadow-md "
            style={{ border: "2px solid rgba(156, 163, 175, 0.2)" }}>
            <div className="w-full items-center relative">
              <div className="px-[18px] h-[48px] flex flex-row justify-between items-center">
                <div className="h-full flex flex-row gap-6 items-center ">
                  {tabs.map((tab, index) => (
                    <div
                      key={index}
                      onClick={() => handleTabClick(tab)}
                      className={`cursor-pointer h-full font-semibold text-center text-[12px] leading-[12px] flex flex-col -tracking-[1%] items-center justify-center ${
                        selectedTab === tab ? "text-p300" : "text-n500"
                      }`}>
                      <p className="flex-grow content-center">{tab}</p>
                      <div
                        className={`w-full h-[2.5px] rounded-md z-30  ${
                          selectedTab === tab ? "bg-p150" : ""
                        }`}></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full h-[2px] bg-n100 absolute bottom-0"></div>
            </div>
            {selectedTab === "Payoff" ? (
              <>
                <div className="relative mt-[9px] flex items-center justify-between px-4">
                  {OILegend}
                  <div className="absolute left-1/2 -translate-x-1/2 flex flex-row z-[400] px-[10px] py-[8px] items-center justify-center text-[10px] font-medium leading-[10px] text-p300 border-n200 border-[1px] rounded-md gap-1">
                    <div className="font-gra">Current Price:</div>
                    <div>{ltp}</div>
                  </div>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-p300 border border-n200 rounded-md hover:bg-n100 transition-colors">
                      <span>Open interest</span>
                      <span className="text-[10px]">
                        ({selectedExpiryDates.length})
                      </span>
                      <svg
                        className={`w-3 h-3 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 top-full mt-1 bg-p400 border border-n200 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                        {availableExpiryDates.map((expiryDate) => (
                          <label
                            key={expiryDate}
                            className="w-full flex px-7 py-2 hover:bg-n100 cursor-pointer text-xs">
                            <input
                              type="checkbox"
                              checked={selectedExpiryDates.includes(expiryDate)}
                              onChange={() => handleExpiryToggle(expiryDate)}
                              className="mr-2"
                            />
                            <span className="text-p300">
                              {formatExpiryDate(expiryDate)}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-[100%] mt-[20px]">
                  <HighchartsReact
                    highcharts={Highcharts}
                    allowChartUpdate={true}
                    options={chartOptions}
                    ref={chartComponent}
                  />
                </div>
              </>
            ) : selectedTab === "Greeks" ? (
              <div className="w-full h-[400px] overflow-y-scroll mt-4">
                {greeksJson.map((option) => {
                  return (
                    <div className=" flex flex-col">
                      <div
                        className=" p-4"
                        style={{
                          borderBottom: "2px solid rgba(156, 163, 175, 0.2)",
                        }}>
                        <div className="flex justify-between items-center">
                          <span className=" text-[13px] text-p300 font-bold">
                            {option.tradingSymbol}
                          </span>
                          <span className=" text-[10px] text-n600">
                            Lots : {option.lotSize} / IV : {option.iv}
                          </span>
                        </div>
                        <div className="mt-1 text-[10px] text-n600">
                          Delta: {option.delta} / Theta: {option.theta} / Gamma:{" "}
                          {option.gamma} / Vega: {option.vega} / Rho:{" "}
                          {option.rho}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="w-full h-[400px] overflow-y-scroll mt-4">
                {selectedData.map((selectedOption, index) => {
                  const estimated =
                    (selectedOption.isBuy ? -1 : 1) *
                    (((selectedOption.price || selectedOption.average_prc) -
                      selectedOption.targetPrice) *
                      selectedOption.lotSize *
                      (selectedOption.lot_multiplier || 1));

                  return (
                    <div className="flex flex-col" key={index}>
                      <div
                        className="p-4"
                        style={{
                          borderBottom: "2px solid rgba(156, 163, 175, 0.2)",
                        }}>
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-[13px] text-p300 font-bold">
                              {selectedOption.tradingSymbol}
                            </span>
                            <div className="mt-1 text-[10px] text-n600">
                              Lots: {selectedOption.lotSize} / Entry:{" "}
                              {selectedOption.price} / Target:{" "}
                              {selectedOption.targetPrice}
                            </div>
                          </div>
                          <div className="flex flex-col justify-center items-center">
                            <span className="text-[10px] text-n600">
                              Estimated P&L
                            </span>
                            <div
                              className="rounded-sm text-[11px] text-white px-4 py-0.5"
                              style={{
                                backgroundColor:
                                  estimated < 0 ? "#E94C69" : "#12B16E",
                              }}>
                              {estimated.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {
              <>
                <div className="w-full h-[2px] bg-n100 "></div>
                <div className="flex flex-row  py-[10px] w-full items-center justify-center gap-[25px]">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <div className="flex flex-row gap-[3px]">
                      <div className="bg-red rounded-l-md w-[7px] h-[3px]"></div>
                      <div className="bg-p150 rounded-r-md w-[7px] h-[3px]"></div>
                    </div>
                    <p className="text-p300 leading-[12px] text-[12px] font-medium">
                      Expiry
                    </p>
                  </div>
                  <div className="flex flex-row items-center justify-center gap-2">
                    <div className="bg-blue rounded-md w-[7px] h-[3px]"></div>
                    <p className="text-p300 leading-[12px] text-[12px] font-medium">
                      Target Date
                    </p>
                  </div>
                </div>
                <div className="w-full h-[2px] bg-n100 mt-1"></div>
                <div className="w-full h-fit flex flex-row py-[10px] gap-[8px] items-center justify-evenly ">
                  <div className="flex flex-row gap-[8px] h-full items-center">
                    <p className="text-[14px] font-sans font-bold text-n500 ">
                      δ
                    </p>
                    <p className="text-[12px] leading-[12px] font-medium text-n500 ">
                      {greeksData.delta}
                    </p>
                  </div>
                  <p className="text-[12px] text-n500">/</p>
                  <div className="flex flex-row h-full gap-[8px] items-center ">
                    <p className="text-[14px]  font-sans font-bold text-n500 ">
                      θ
                    </p>
                    <p className="text-[12px] leading-[12px] font-medium text-n500 ">
                      {greeksData.theta}
                    </p>
                  </div>
                  <p className="text-[12px] text-n500">/</p>
                  <div className="flex flex-row  h-full gap-[8px] items-center">
                    <p className="text-[14px]  font-sans font-bold text-n500">
                      γ
                    </p>
                    <p className="text-[12px] leading-[12px] font-medium text-n500 ">
                      {greeksData.gamma}
                    </p>
                  </div>
                  <p className="text-[12px] text-n500">/</p>
                  <div className="flex flex-row  h-full gap-[8px] items-center">
                    <p className="text-[14px] font-sans font-bold text-n500">
                      ν
                    </p>
                    <p className="text-[12px] leading-[12px] font-medium text-n500 ">
                      {greeksData.vega}
                    </p>
                  </div>
                  <p className="text-[12px] text-n500">/</p>
                  <div className="flex flex-row  h-full gap-[8px] items-center">
                    <p className="text-[14px] font-sans font-bold text-n500">
                      ρ
                    </p>
                    <p className="text-[12px] leading-[12px] font-medium text-n500 ">
                      {greeksData.rho}
                    </p>
                  </div>
                </div>
                <div className="w-full h-[2px] bg-n100 mt-1"></div>
                <div className="py-[10px] px-[28px]">
                  <div className="h-[32px] flex flex-row items-center justify-between py-[10px] ">
                    <span className="text-n500 text-[14px] leading-[14px] font-medium font-gra">
                      Standard Deviation{" "}
                    </span>
                    <span>
                      <div className="w-[123px] h-[32px] flex flex-row border-n000 border-2 rounded-md  text-center items-center justify-between pr-[16px] pl-[16px] pt-[8px] pb-[8px]">
                        <div
                          role="button"
                          onClick={decrementLots}
                          className="flex justify-center items-center h-[16px] w-[16px] ">
                          <FiMinus className="text-n600 text-[14px] text-center" />
                        </div>
                        <span className="text-[14px] text-n600 font-semibold leading-[14px]">
                          {padLotsValue(lotsValue)}
                        </span>
                        <div
                          role="button"
                          onClick={incrementLots}
                          className="flex justify-center items-center h-[16px] w-[16px] ">
                          <FaPlus className="text-n600 text-[14px] text-center" />
                        </div>
                      </div>
                    </span>
                  </div>
                </div>
              </>
            }

            <ReactTooltip
              id="std-deviation"
              place="top"
              content="Helps determine the spread of asset prices from their average price."
              variant={theme === "dark" ? "light" : "dark"}
              className="mobile-tooltip"
            />
            <ReactTooltip
              id="total-est-pnl"
              place="top"
              content="Estimated P&L on selected target day"
              variant={theme === "dark" ? "light" : "dark"}
              className="mobile-tooltip"
            />
          </div>
          {
            <TargetSlidersMobile
              expiries={expiries}
              daysleft={daysleft}
              handleDateClick={handleDateClick}
              selectedDate={selectedDate}
              isParamsValid={isParamsValid}
              script={selectedData[0].scrip}
              setLtpLine={setLtpLine}
              handleLtpLineChange={handleLtpLineChange}
              initialLt={ltp}
              lowerBand1Value={lowerBand1Val}
              upperBand1Value={upperBand1Val}
              lowerBand2Value={lowerBand2Val}
              upperBand2Value={upperBand2Val}
              greeksData={greeksJson}
              setGreeksData={setGreeksData}
            />
          }
        </>
      ) : (
        <div className="w-[358px] h-[567px] bg-p400 shadow-xl rounded-md"></div>
      )}
    </>
  );
};
export default StrategyGraph;
